import { useEffect, useState } from 'react';
import { formatMoney } from './documents/currencies';

const CODES = ['RUB'];
const ENDPOINT = `https://api.fxratesapi.com/latest?base=USD&currencies=${CODES.join(',')}`;
const CACHE_KEY = 'landingPriceRates.v1';
const TTL_MS = 6 * 60 * 60 * 1000;

function readCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (cached?.rates && Date.now() - cached.storedAt < TTL_MS) return cached.rates;
  } catch { /* Storage can be unavailable in private browsing. */ }
  return null;
}

export default function usePriceEquivalents() {
  const [rates, setRates] = useState(readCache);

  useEffect(() => {
    if (rates) return undefined;
    let alive = true;
    (async () => {
      try {
        const response = await fetch(ENDPOINT);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const nextRates = data?.rates;
        if (!CODES.every((code) => Number.isFinite(nextRates?.[code]) && nextRates[code] > 0)) return;
        if (!alive) return;
        setRates(nextRates);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ rates: nextRates, storedAt: Date.now() }));
        } catch { /* The current page can still use the fetched rates. */ }
      } catch {
        // Keep the USD prices visible if the rate service is unavailable.
      }
    })();
    return () => { alive = false; };
  }, [rates]);

  const rubFromUsd = (price, roundTo = 50) => {
    if (!rates?.RUB) return '';
    const numbers = String(price).match(/\d+(?:[.,]\d+)?/g);
    if (!numbers) return '';
    const parts = numbers.map((part) => {
      const amount = Math.round((parseFloat(part.replace(',', '.')) * rates.RUB) / roundTo) * roundTo;
      return formatMoney(amount, 'RUB');
    });
    return parts.join('–');
  };

  const usdFromRub = (price) => {
    if (!rates?.RUB) return '';
    const number = String(price).match(/\d+(?:[.,]\d+)?/);
    if (!number) return '';
    return formatMoney(parseFloat(number[0].replace(',', '.')) / rates.RUB, 'USD');
  };

  return { rubFromUsd, usdFromRub };
}
